import { Fragment, useCallback, useEffect, useRef } from 'react';

import { ReasoningStep } from '@mint-lib/ai-chat';
import {
  AiMessage,
  ChatHistorySkeleton,
  ReasoningStepsLoader,
  UserMessage,
} from '@mint-lib/ai-chat/components';
import { Chat } from '@mint-sdk/api-ai-assistant';
import { formatDate, isToday, isYesterday } from 'date-fns';

type Props = {
  chatMessages: Chat.ChatMessageRecordType[];
  reasoningMessages: ReasoningStep[];
  chatId: string;
  isAiProcessing: boolean;
  isLoading: boolean;
};

const ChatConversation = ({
  chatId,
  chatMessages,
  reasoningMessages,
  isAiProcessing,
  isLoading,
}: Props) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    anchorRef?.current?.scrollIntoView({ block: 'end' });
  }, []);

  const scrollToLoader = useCallback(
    (node: HTMLDivElement) => {
      node?.scrollIntoView({ behavior: 'smooth' });
    },
    [isAiProcessing],
  );

  const reasoningLoaderData = reasoningMessages.find((rm) => !rm.isDone)?.data;

  const messagesByGroup = Object.entries(
    chatMessages.reduce(
      (result, message) => {
        const date = message.createdAt;
        let key;

        if (isToday(date)) {
          key = 'Today';
        } else if (isYesterday(date)) {
          key = 'Yesterday';
        } else {
          key = formatDate(date, 'dd/MM/yyyy');
        }

        if (!result[key]) {
          result[key] = [];
        }
        result[key].push(message);
        return result;
      },
      {} as Record<string, Chat.ChatMessageRecordType[]>,
    ),
  );

  if (isLoading) {
    return <ChatHistorySkeleton />;
  }

  return (
    <div className="flex w-full flex-col gap-6 pb-4" ref={anchorRef}>
      {messagesByGroup.map(([date, messages]) => (
        <Fragment key={date}>
          {messages?.map((chat) =>
            chat.message.type === 'ai' ? (
              <AiMessage
                date={date}
                key={chat.messageId}
                chatId={chatId}
                additionalMetadata={chat.message.additionalMetadata}
                isSystemError={chat.message.isSystemError}
                text={chat.message.content}
                messageId={chat.messageId}
                shouldBeVisibleExtraContent={false}
                knowledgeBaseSources={
                  chat.message.additionalMetadata.metadata?.knowledgeBaseSources
                }
                webSources={
                  chat.message.additionalMetadata.metadata?.webSearchSources
                }
                reasoningData={
                  reasoningMessages.find(
                    (rm) => rm.messageId === chat.messageId,
                  )?.data
                }
              />
            ) : (
              <UserMessage key={chat.messageId} text={chat.message.content} />
            ),
          )}
        </Fragment>
      ))}

      {isAiProcessing && (
        <div ref={scrollToLoader}>
          <ReasoningStepsLoader reasoningData={reasoningLoaderData} />
        </div>
      )}
    </div>
  );
};

export { ChatConversation };
